import { Box, Flex, InputProps, Input as NativeInput, Skeleton } from "@chakra-ui/react"
import { HTMLInputTypeAttribute, ReactNode } from "react";
import { SPACING } from "../generic/config";
import Text from "./Text";
import useModel, { getFieldValidity } from "./hooks/useModel";
import Icon, { Icons } from "./Icons";
import Tooltip from "./Tooltip";
import { Switch, SwitchProps } from "@/components/ui/switch";
import { isSameDay } from "@/generic/date-utils";
import { DropdownItem } from "@/api/base/dropdown-item";
import Dropdown from "./Dropdown";

type DropdownModelInputProps = {
    placeholder?: string,
    noSelectionTitle?: string,
    items: DropdownItem[],
    onChange?: (item: DropdownItem | null) => void,
};

type GenericModelInputProps<A> = {
    children?: ReactNode,
    title: string | null,
    model: ReturnType<typeof useModel<A>>["model"],
    type?: HTMLInputTypeAttribute,
    fieldKey: keyof A,
    isDisabled?: boolean,
    size?: "sm" | "md" | "lg",
} & Omit<InputProps, "title"> & Omit<SwitchProps, "title">;

type ModelInputProps<A> = (GenericModelInputProps<A> & { kind?: "generic" }) | (GenericModelInputProps<A> & { kind: "dropdown", dropdownProps: DropdownModelInputProps });

const ModelInput = <A,>({
    kind = "generic",
    title,
    type = "text",
    model,
    fieldKey,
    isDisabled,
    size = "md",
    ...rest
}: ModelInputProps<A>) => {
    const emptyBaseValue = model.typeProviderObject[fieldKey];
    const dropdownProps = kind === "dropdown" && "dropdownProps" in rest ? rest.dropdownProps : undefined;

    const getInputType = (): "input" | "switch" | "dropdown" | "calendar" | "calendar-optional" => {
        if (kind === "dropdown") {
            return "dropdown";
        }

        switch (typeof emptyBaseValue) {
            case "boolean":
                return "switch";
            case "object":
                if (emptyBaseValue instanceof Date) {
                    if (isSameDay(emptyBaseValue, new Date("1900-01-01"))) {
                        return "calendar-optional";
                    }

                    return "calendar";
                }
                return "dropdown";
            default:
                return "input";
        }
    };

    const inputType = getInputType();

    const initialValue = model.initialObject[fieldKey];
    const maybeDefaultValue = model.object[fieldKey];
    const defaultValue = maybeDefaultValue === null || maybeDefaultValue === undefined ? emptyBaseValue : maybeDefaultValue;
    const isDirty = model.object[fieldKey] !== model.initialObject[fieldKey];
    const validity = getFieldValidity(model.validationMessages, fieldKey);
    const validationLines = validity.messages.map(message => `${message.severity === "error" ? "❌" : "⚠️"} ${message.description}`);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let cleanedValue = e.target.value;
        console.log("ModelInput onChange", e.target.name, cleanedValue);
        if (inputType === "calendar" && !e.target.value) {
            cleanedValue = new Date().toISOString().split("T")[0]; // Default to today if no date is selected
            e.target.value = cleanedValue; // Update the input value
            e.target.defaultValue = cleanedValue; // Update the default value
            console.warn("No date selected, defaulting to today:", cleanedValue);
        }

        if (inputType === "calendar-optional" && !e.target.value) {
            cleanedValue = "1900-01-01"; // Use sentinel date for optional date
            e.target.value = cleanedValue;
            e.target.defaultValue = cleanedValue;
            console.warn("No date selected for optional calendar, defaulting to sentinel date:", cleanedValue);
        }

        const finalValue = ["calendar", "calendar-optional"].includes(inputType) ? new Date(cleanedValue) : cleanedValue;
        model.setObject(prevState => {
            if (prevState) {
                return { ...prevState, [e.target.name]: finalValue };
            }

            return prevState;
        });
    };

    const onChangeSwitch = (e: React.FormEvent<HTMLLabelElement>) => {
        model.setObject(prevState => {
            if (prevState) {
                return { ...prevState, [fieldKey]: !prevState[fieldKey] };
            }

            return prevState;
        });
    }

    const onChangeDropdown = (item: DropdownItem | null) => {
        model.setObject(prevState => {
            if (prevState) {
                return { ...prevState, [fieldKey]: item ? item.id : null };
            }

            return prevState;
        });

        dropdownProps?.onChange?.(item);
    }

    const elementKey = `${fieldKey.toString()}-${type}-${initialValue}`; // Used to re-render for example date on load!
    return (
        <Box data-key={elementKey} key={fieldKey.toString()} m={SPACING / 4} textAlign={"start"}>
            <Flex alignItems={"center"}>
                {title && (
                    <Text weight={isDirty ? "bolder" : "semibold"} variant={size === "sm" ? "small" : (size === "md" ? "normal" : "title")} mb={SPACING / 4}>
                        {title}
                        {model.object[fieldKey] !== model.initialObject[fieldKey] ? "*" : ""}
                    </Text>
                )}
                {!validity.isValid && (
                    <Tooltip tooltip={<span style={{ fontSize: 14 }}>{validationLines.map((line, i) => (<span key={i}>{i > 0 && <br />}{line}</span>))}</span>} >
                        <Icon size="xs" icon={Icons.Warning} color={validity.severity === "error" ? "red" : "orange"} ml={SPACING / 2} cursor="help" />
                    </Tooltip>
                )}
            </Flex>
            <Skeleton loading={model.isLoading}>
                {inputType === "input" && (
                    <NativeInput key={elementKey} size={size} mb={SPACING / 4} disabled={isDisabled} defaultValue={defaultValue as string} {...rest} type={type} onChange={onChange} id={fieldKey.toString()} name={fieldKey.toString()} />
                )}

                {inputType === "switch" && (
                    <Switch key={elementKey} size={size} mb={SPACING / 4} disabled={isDisabled} defaultChecked={defaultValue as boolean} {...rest} id={fieldKey.toString()} name={fieldKey.toString()} onChange={onChangeSwitch} />
                )}

                {["calendar", "calendar-optional"].includes(inputType) && "toISOString" in new Date(defaultValue as string) && (
                    <NativeInput
                        key={elementKey}
                        id={fieldKey.toString()}
                        name={fieldKey.toString()}
                        size={size}
                        mb={SPACING / 4}
                        disabled={isDisabled}
                        defaultValue={new Date(defaultValue as string).toISOString().split("T")[0]}
                        value={inputType === "calendar-optional" && isSameDay(new Date(defaultValue as string), new Date("1900-01-01")) ? "" : undefined}
                        {...rest}
                        type="date"
                        onChange={onChange}
                    />
                )}

                {inputType === "dropdown" && kind === "dropdown" && dropdownProps && (
                    <Dropdown
                        items={dropdownProps.items}
                        value={dropdownProps.items.find(item => item.id === (model.object[fieldKey] as string)) || null}
                        placeholder={dropdownProps.placeholder} noSelectionTitle={dropdownProps.noSelectionTitle}
                        onChange={onChangeDropdown}
                    />
                )}
            </Skeleton>
        </Box>
    )
}

export default ModelInput;
